/// <amd-module name="VSS/Features/Identities/IdentityService" />
import {
    IVssPageContext,
    IVssRestClient,
    IVssRestClientOptions,
    IVssService,
    RestClients,
    ServiceOptions,
    Services,
    VssService
} from "VSS/Platform/Context";
import { ClientTraceEvent, Level } from "VSS/Platform/Contracts/ClientTrace";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { trace } from "VSS/Platform/Trace";

//#region internal API

// Search API
interface QueryTokenResultModel {
    queryToken: string;
    identities: IIdentity[];
    pagingToken?: string;
}

/**
 * @exemptedapi
 */
export interface IdentitiesSearchResponseModel {
    results: QueryTokenResultModel[];
}

// GetConnections API

/**
 * @exemptedapi
 */
interface IdentitiesGetConnectionsRequestModel {
    connectionTypes: string[];
    identityTypes: string[];
    operationScopes: string[];
    depth?: number;
    options?: any;
    pagingToken?: string;
    properties?: string[];
}

// GetMru API

/**
 * @exemptedapi
 */
interface IdentitiesGetMruRequestModel {
    operationScopes: string[];
    properties?: string[];
    filterByAncestorEntityIds?: string[];
    filterByEntityIds?: string[];
    maxItemsCount?: number;
}

/**
 * @exemptedapi
 */
interface IdentitiesGetMruResponseModel {
    mruIdentities: IIdentity[];
}

// PatchMru API

/**
 * @exemptedapi
 */
interface IdentitiesPatchMruAction {
    op: string;
    value: string[];
    operationScopes: string[];
}

/**
 * @exemptedapi
 */
interface IdentitiesPatchMruResponseModel {
    result: boolean;
}

export interface IIdentityPickerHttpClient extends IVssRestClient {
    beginGetIdentities(identitiesRequest: IdentitiesSearchRequestModel): Promise<IdentitiesSearchResponseModel>;
    beginGetConnections(objectId: string, getRequestParams: IdentitiesGetConnectionsRequestModel): Promise<IdentitiesGetConnectionsResponseModel>;
    beginGetIdentityFeatureMru(
        identityId: string,
        featureId: string,
        getRequestParams: IdentitiesGetMruRequestModel
    ): Promise<IdentitiesGetMruResponseModel>;
    beginPatchIdentityFeatureMru(
        identityId: string,
        featureId: string,
        patchRequestBody: IdentitiesPatchMruAction[]
    ): Promise<IdentitiesPatchMruResponseModel>;
    getRootPathPromise(): Promise<string>;
}

class IdentityPickerHttpClient extends RestClientBase implements IIdentityPickerHttpClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    public async beginGetIdentities(identitiesRequest: IdentitiesSearchRequestModel): Promise<IdentitiesSearchResponseModel> {
        return this.beginRequest<IdentitiesSearchResponseModel>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "_apis/IdentityPicker/Identities",
            method: "POST",
            body: identitiesRequest
        });
    }

    public async beginGetConnections(
        objectId: string,
        getRequestParams: IdentitiesGetConnectionsRequestModel
    ): Promise<IdentitiesGetConnectionsResponseModel> {
        return this.beginRequest<IdentitiesGetConnectionsResponseModel>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "_apis/IdentityPicker/Identities/{objectId}/connections",
            routeValues: {
                objectId: objectId
            },
            queryParams: getRequestParams
        });
    }

    public async beginGetIdentityFeatureMru(
        objectId: string,
        featureId: string,
        getRequestParams: IdentitiesGetMruRequestModel
    ): Promise<IdentitiesGetMruResponseModel> {
        return this.beginRequest<IdentitiesGetMruResponseModel>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "_apis/IdentityPicker/Identities/{objectId}/mru/{featureId}",
            routeValues: {
                objectId: objectId,
                featureId: featureId
            },
            queryParams: getRequestParams
        });
    }

    public async beginPatchIdentityFeatureMru(
        objectId: string,
        featureId: string,
        patchRequestBody: IdentitiesPatchMruAction[]
    ): Promise<IdentitiesPatchMruResponseModel> {
        return this.beginRequest<IdentitiesPatchMruResponseModel>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "_apis/IdentityPicker/Identities/{objectId}/mru/{featureId}",
            method: "PATCH",
            routeValues: {
                objectId: objectId,
                featureId: featureId
            },
            body: patchRequestBody
        });
    }

    public async getRootPathPromise(): Promise<string> {
        return this.getRootPath();
    }
}

RestClients.add("IIdentityPickerHttpClient", {
    factory: IdentityPickerHttpClient,
    options: {
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});

//#endregion

/**
 * @exemptedapi
 */
export interface IdentitiesGetConnectionsResponseModel {
    successors?: IIdentity[];
    managers?: IIdentity[];
    directReports?: IIdentity[];
}

/**
 * @exemptedapi
 */
export interface IdentitiesSearchRequestModel {
    query: string;
    identityTypes?: string[];
    operationScopes?: string[];
    queryTypeHint?: string;
    pagingToken?: string;
    properties?: string[];
    filterByAncestorEntityIds?: string[];
    filterByEntityIds?: string[];
    options?: any;
}

export interface IIdentity {
    entityId: string;
    entityType: string;
    originDirectory: string;
    originId: string;
    displayName?: string;
    mail?: string;
    localDirectory?: string;
    localId?: string;
    scopeName?: string;
    department?: string;
    jobTitle?: string;
    mailNickname?: string;
    physicalDeliveryOfficeName?: string;
    signInAddress?: string;
    subjectDescriptor?: string;
    surname?: string;
    guest?: boolean;
    active?: boolean;
    description?: string;
    image?: string;
    manager?: string;
    samAccountName?: string;
    telephoneNumber?: string;
    isMru?: boolean;
    isHosted?: boolean;
}

/**
 * Name of the VssIdentityService
 */
export const VssIdentityServiceName = "ms.vss-features.identity-service";

/**
 * A service that gets a filtered list of identities as well as the MRU identities
 */
export interface IVssIdentityService extends IVssService {
    /**
     * Get a list of the Most Recently Used (MRU) identities
     *
     * @returns list of the Most Recently Used (MRU) identities
     */
    getIdentityMruAsync(maxItemsCount?: number): Promise<IIdentity[]>;

    /**
     * Given a search request model, return a list of Entities. If the filterIdentity callback is added, additionally filter the values before returning
     *
     * @param query The query to search the identities type with.
     * @param identityTypes The types of identities to search (default "user" and "group")
     * @param operationScopes The scope you would like to search (default "ims", "source")
     * @param queryTypeHint A hint of what property your query is trying to search
     * @param filterIdentity A filter passed in to alter the results of the identities found
     * @param options Additional options to pass into the search
     * @returns The list of filtered identities from the search.
     */
    searchIdentitiesAsync(
        query: string,
        identityTypes?: string[],
        operationScopes?: string[],
        queryTypeHint?: string,
        options?: any,
        filterIdentity?: (returnedEntities: IIdentity[]) => IIdentity[]
    ): Promise<IIdentity[]>;

    /**
     * Add a list of identities to the MRU
     *
     * @param identities list of IdentityRefs to add to the MRU
     * @returns True if the item was added, false otherwise
     */
    addMruIdentitiesAsync(identities: IIdentity[]): Promise<boolean>;

    /**
     * Remove a list of identities from the MRU
     *
     * @param identities list of IdentityRefs to remove from the MRU
     * @returns True if the item was removed, false otherwise
     */
    removeMruIdentitiesAsync(identity: IIdentity[]): Promise<boolean>;

    /**
     * Gets a list of connections for a given identity
     *
     * @param identity Entity to look up connections
     * @returns Connections for the given identity
     */
    getConnections(identity: IIdentity, getDirectReports?: boolean): Promise<IdentitiesGetConnectionsResponseModel>;
}

/**
 * Interface of identity service supporting "searchIdentitiesAsync" operation
 */
export type IVssIdentitySearchService = IVssService & Pick<IVssIdentityService, "searchIdentitiesAsync">;

const defaultProperties = ["DisplayName", "IsMru", "ScopeName", "SamAccountName", "Active", "SubjectDescriptor"];
const defaultUserProperties = [
    "Department",
    "JobTitle",
    "Mail",
    "MailNickname",
    "PhysicalDeliveryOfficeName",
    "SignInAddress",
    "Surname",
    "Guest",
    "TelephoneNumber",
    "Manager"
];
const defaultGroupProperties = ["Description"];
export const defaultQueryProperties = defaultProperties.concat(defaultUserProperties).concat(defaultGroupProperties);

export class VssIdentityService extends VssService implements IVssIdentityService {
    protected _identityPickerClient: IIdentityPickerHttpClient;
    protected _mruCache: IIdentity[] | undefined;
    private _pageData: IPageData;
    private _pageContext: IVssPageContext;
    private static readonly area = "IdentityService";
    private static readonly feature = "MruCache";

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this._pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!;
        this._identityPickerClient = pageContext.getRestClient<IIdentityPickerHttpClient>("IIdentityPickerHttpClient");
        this._pageContext = pageContext;
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);
        delete this._mruCache;
    }

    public async getIdentityMruAsync(maxItemsCount?: number): Promise<IIdentity[]> {
        if (!this._mruCache) {
            const requestParams: IdentitiesGetMruRequestModel = {
                operationScopes: ["ims"],
                properties: defaultQueryProperties,
                maxItemsCount: maxItemsCount
            };
            const rootPath = await this._identityPickerClient.getRootPathPromise();
            const mru = await this._identityPickerClient.beginGetIdentityFeatureMru("me", "common", requestParams);
            this._mruCache = mru.mruIdentities.map(x => this._addAdditionalIdentityInfo(x, rootPath));
        }
        return this._mruCache;
    }

    public async searchIdentitiesAsync(
        query: string,
        identityTypes?: string[],
        operationScopes?: string[],
        queryTypeHint?: string,
        options?: any,
        filterIdentity?: (returnedEntities: IIdentity[]) => IIdentity[]
    ): Promise<IIdentity[]> {
        const requestProps: IdentitiesSearchRequestModel = {
            query: query,
            identityTypes: identityTypes || ["user", "group"],
            operationScopes: operationScopes || ["ims", "source"],
            queryTypeHint: queryTypeHint,
            options: {
                MinResults: 5,
                MaxResults: 40,
                ...options
            },
            properties: defaultQueryProperties
        };

        const rootPath = await this._identityPickerClient.getRootPathPromise();
        const identityList = await this._identityPickerClient.beginGetIdentities(requestProps);

        // Note that Map object preserves the insert order
        const map = new Map<string, IIdentity>();

        // Add all MRU values that match the search terms.
        // We prefer MRU values to appear first in the results (with the caveat that the filterIdentity
        // function may sort the results)
        const getMatchingMruIdentities = this._createMruFilter(requestProps);
        for (const resultSet of identityList.results) {
            for (const identity of getMatchingMruIdentities(resultSet.queryToken)) {
                map.set(identity.entityId, identity);
            }
        }

        // Add the retrieved identities (with additional identity info).
        // If a matching MRU value exists, don't overwrite it with the retrieved values as this could
        // cause UI to suddenly switch display name if the retrieved value had changed on the server
        for (const resultSet of identityList.results) {
            for (const filteredIdentity of resultSet.identities) {
                if (!map.has(filteredIdentity.entityId)) {
                    map.set(filteredIdentity.entityId, this._addAdditionalIdentityInfo(filteredIdentity, rootPath));
                }
            }
        }

        const identities: IIdentity[] = [];
        map.forEach(identity => {
            identities.push(identity);
        });

        return filterIdentity ? filterIdentity(identities) : identities;
    }

    public async addMruIdentitiesAsync(identity: IIdentity[]): Promise<boolean> {
        const requestParams: IdentitiesPatchMruAction[] = [
            {
                operationScopes: ["ims", "source"],
                op: "add",
                value: identity.map(x => x.localId || x.entityId)
            }
        ];

        try {
            const mru = await this._identityPickerClient.beginPatchIdentityFeatureMru("me", "common", requestParams);
            const event: ClientTraceEvent = {
                area: VssIdentityService.area,
                feature: VssIdentityService.feature,
                level: Level.Info,
                message: `New identity ${requestParams[0].value} was added to MRU.`,
                properties: {
                    userAddedToMru: requestParams[0].value
                },
                component: "VssIdentityService",
                method: "addMruIdentitiesAsync",
                exceptionType: ""
            };
            trace(this._pageContext, event);

            return mru.result;
        } catch (error) {
            const event: ClientTraceEvent = {
                area: VssIdentityService.area,
                feature: VssIdentityService.feature,
                level: Level.Info,
                message: `${requestParams[0].value} identity selected from MRU was removed or marked as inactive. Clean MRU cache.`,
                properties: {
                    userAddedToMru: requestParams[0].value,
                    error: error.message
                },
                component: "VssIdentityService",
                method: "addMruIdentitiesAsync",
                exceptionType: ""
            };
            trace(this._pageContext, event);

            // Remove MRU cache as if we have error here, it's invalid.
            delete this._mruCache;
            throw error;
        }
    }

    public async removeMruIdentitiesAsync(identity: IIdentity[]): Promise<boolean> {
        const requestParams: IdentitiesPatchMruAction[] = [
            {
                operationScopes: ["ims", "source"],
                op: "remove",
                value: identity.map(x => x.localId || x.entityId)
            }
        ];
        return this._identityPickerClient.beginPatchIdentityFeatureMru("me", "common", requestParams).then(x => x.result);
    }

    public async getConnections(identity: IIdentity, getDirectReports?: boolean): Promise<IdentitiesGetConnectionsResponseModel> {
        const isUser = identity.entityType.trim().toLowerCase() === "user";
        const requestParams: IdentitiesGetConnectionsRequestModel = {
            identityTypes: ["user", "group", "servicePrincipal"],
            operationScopes: isUser ? (identity.originDirectory === "ad" ? ["source", "ims", "ad"] : ["source"]) : ["ims", "source"],
            connectionTypes: isUser ? (getDirectReports ? ["managers", "directReports"] : ["managers"]) : ["successors"],
            depth: isUser && getDirectReports ? 6 : 1,
            properties: defaultQueryProperties
        };
        const rootPath = await this._identityPickerClient.getRootPathPromise();
        return this._identityPickerClient.beginGetConnections(identity.entityId, requestParams).then(x => {
            return {
                managers: x.managers ? this._addInfoToIdentities(x.managers, rootPath) : [],
                directReports: x.directReports ? this._addInfoToIdentities(x.directReports, rootPath) : [],
                successors: x.successors ? this._addInfoToIdentities(x.successors, rootPath) : []
            };
        });
    }

    protected _createMruFilter(requestProps: IdentitiesSearchRequestModel): (queryToken: string) => IIdentity[] {
        const { _mruCache } = this;
        if (_mruCache) {
            let byIdentityType = (identity: IIdentity) => true;
            if (requestProps.identityTypes) {
                const idTypeSet = new Set(requestProps.identityTypes.map(t => t.toLocaleLowerCase()));
                byIdentityType = identity => idTypeSet.has(identity.entityType.toLocaleLowerCase());
            }

            return (queryToken: string) => {
                queryToken = queryToken.toLowerCase();
                return _mruCache
                    .filter(byIdentityType)
                    .filter(identity => identity.displayName && identity.displayName.toLowerCase().startsWith(queryToken));
            };
        }

        return () => [];
    }

    private _getSignInAddress(identity: IIdentity): string {
        if (!this._pageData.isHosted && identity.samAccountName && identity.samAccountName.trim()) {
            return (identity.scopeName && identity.scopeName.trim() ? identity.scopeName + "\\" : "") + identity.samAccountName;
        }
        return identity.signInAddress && identity.signInAddress.trim()
            ? identity.signInAddress
            : identity.mail && identity.mail.trim()
            ? identity.mail
            : identity.mailNickname && identity.mailNickname.trim()
            ? identity.mailNickname
            : "";
    }

    private _addInfoToIdentities(identities: IIdentity[], rootPath: string): IIdentity[] {
        return identities.map(x => this._addAdditionalIdentityInfo(x, rootPath));
    }

    protected _addAdditionalIdentityInfo(identity: IIdentity, rootPath: string): IIdentity {
        return {
            ...identity,
            image: identity.subjectDescriptor ? `${rootPath}_apis/GraphProfile/MemberAvatars/${identity.subjectDescriptor}` : undefined,
            mail: this._getSignInAddress(identity),
            isHosted: this._pageData.isHosted
        };
    }
}

Services.add(VssIdentityServiceName, { serviceFactory: VssIdentityService, options: ServiceOptions.Remotable });

// Keep for compat reasons.
Services.add("IIVssIdentityService", { serviceFactory: VssIdentityService, options: ServiceOptions.Remotable });
